import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CategoryRow,
  ChannelRow,
  CreatorPerformancePayload,
  CreatorPerformanceState,
  getCreatorPerformance,
  HistoryChannel,
  historyKey,
  refreshCreatorPerformance,
} from '../../services/creatorPerformance'
import './creator-performance.css'

export const POLL_MS = 5000

const money = (v: number | null | undefined) =>
  v == null ? '-' : `HK$${Math.round(v).toLocaleString('en-US')}`
const int = (v: number | null | undefined) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'))
const pct = (v: number | null | undefined) => (v == null ? '-' : `${(v * 100).toFixed(0)}%`)
const dec = (v: number | null | undefined, digits: number) => (v == null ? '-' : v.toFixed(digits))

const formatElapsed = (startedAt: string | null, now: number) => {
  if (!startedAt) return ''
  const s = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

const STORE_NAME = { app_store: 'App Store', google_play: 'Google Play' } as const

/** Which payers this refresh counts, and any store it could not read. */
function storesNote(payload: CreatorPerformancePayload): string {
  const head = 'All amounts are in HKD.'
  if (!payload.stores) {
    return `${head} This refresh counts web (Stripe) payments only. Press Refresh to add App Store and Google Play subscribers.`
  }
  let text = `${head} App Store and Google Play revenue is what maayot receives after the store's cut; web revenue is what the learner paid.`
  for (const s of ['app_store', 'google_play'] as const) {
    const { status } = payload.stores[s]
    if (status === 'counted') continue
    const why = status === 'not_configured' ? 'the server has no credentials for it' : 'the store returned an error'
    text += ` ${STORE_NAME[s]} subscribers are not counted on this refresh because ${why}, so channels that send many app users look weaker than they are.`
  }
  return text
}

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'never'

const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })

// Header tooltips. Adapted from the dashboard marketing/analyze.py renders.
const TIP = {
  subs: 'Paid subscribers attributed to this row, whether they pay on the web, in the App Store or on Google Play.',
  revenue: "Net revenue these subscribers have paid to date, in HKD. App Store and Google Play payments count what maayot receives after the store's cut.",
  avg: 'Revenue divided by subscribers: what one subscriber has paid so far. Observed, not projected.',
  mo: 'Share of subscribers billed monthly. Monthly-heavy rows build LTV slowly over many small payments; annual-heavy rows capture cash upfront.',
  age: 'Median months since first payment. A young cohort has most of its LTV still to come.',
  maturity: 'Fraction of eventual lifetime revenue a cohort of this age has typically paid already. 1.0 means little more is expected.',
  prior: 'What an average subscriber in this category is worth, leaving this channel out. Used when the channel has little data of its own.',
  w: 'How much the estimate trusts this channel\'s own data (0 to 1) rather than the category average. Grows with subscribers and cohort age.',
  ltv: 'Projected lifetime revenue per subscriber, in HKD: this channel\'s observed revenue projected forward by cohort age, blended with the category average by w.',
  p25: 'Cautious spend ceiling: subscribers x low LTV* x payback. Below this, renew at full scope.',
  p50: 'Decision number: subscribers x LTV* x payback. Below this, the campaign pays back at the central estimate.',
  p75: 'Optimistic spend ceiling: subscribers x high LTV* x payback. Above this, only the best case pays back.',
  recent: 'Paid subscribers whose first payment was in the 90 days before the data date. Shows whether a channel is still producing.',
  signups: 'Secondary signal. Free signups that named this channel in the in-app survey. Not a measure of value.',
  conversion: 'Signups from the in-app survey who went on to pay. Secondary signal.',
  newPayers: 'Signups who have not paid yet but are expected to, at the low end of this row\'s conversion rate and given how long ago they signed up.',
  worth: 'What this has been worth, to compare against what you paid. With paid subscribers: ETS p50 (subscribers x LTV* x payback). With none yet: the conservative value of the signups it brought, marked "from signups".',
  perSignup: 'What one brand-new signup from here is worth today, conservatively: the most to pay per signup in a per-signup deal.',
  pipeline: 'Conservative value of signups who have not paid yet: low-end conversion rate x chance they still convert given how long ago they signed up x low-end LTV x payback. A creator with no payers is never credited more than an average payer\'s LTV.',
}

type SortKey =
  | 'channel' | 'category' | 'subscribers' | 'revenue' | 'avg_revenue' | 'monthly_share'
  | 'cohort_age_months' | 'maturity' | 'shrinkage_w' | 'ltv_star' | 'ets_p25' | 'ets_p50'
  | 'ets_p75' | 'recent_subscribers_90d' | 'signups' | 'conversion' | 'pipeline' | 'worth'

/** Signup-to-paid within the in-app survey cohort, or null with no signups. */
const conversion = (r: { signups: number; paid_from_signups: number }) =>
  r.signups ? r.paid_from_signups / r.signups : null

/** Cohort figures only mean something once a row has payers of its own. */
const ifPaid = (r: { subscribers: number }, text: string) => (r.subscribers > 0 ? text : '-')

const ets = (row: { subscribers: number; ltv_star: number; ltv_star_p25: number; ltv_star_p75: number }, payback: number) => ({
  p25: row.subscribers * row.ltv_star_p25 * payback,
  p50: row.subscribers * row.ltv_star * payback,
  p75: row.subscribers * row.ltv_star_p75 * payback,
})

/**
 * Change since the previous refresh, shown muted next to a figure. Nothing when
 * there is no previous refresh or no change; "new" for a row that did not exist.
 */
const Delta: React.FC<{ show: boolean; now: number; before: number | undefined }> = ({ show, now, before }) => {
  if (!show) return null
  if (before === undefined) return <span className='cp-delta'> new</span>
  const d = Math.round(now - before)
  if (!d) return null
  return <span className='cp-delta'> {d > 0 ? `+${d}` : d}</span>
}

/** ETS p50 for rows with payers; the conservative signup value, marked, for rows without. */
const Worth: React.FC<{ row: { worth: number; worth_basis: 'ets_p50' | 'signups' }; scale: number }> = ({ row, scale }) => (
  <>
    {money(row.worth * scale)}
    {row.worth_basis === 'signups' ? <span className='cp-muted cp-basis'> from signups</span> : null}
  </>
)

const CategoryCell: React.FC<{ row: ChannelRow }> = ({ row }) => {
  const bd = row.category_breakdown || []
  if (bd.length <= 1) return <>{row.category}</>
  const title = bd.map((b) => `${b.category}: ${b.subscribers}`).join(', ')
  return (
    <span className='cp-merged' title={`Merged across categories. ${title}`}>
      {row.category} <span className='cp-muted'>merged</span>
    </span>
  )
}

const Th: React.FC<{
  label: string
  tip?: string
  sortKey?: SortKey
  sort?: { key: SortKey; dir: 'asc' | 'desc' }
  onSort?: (k: SortKey) => void
  left?: boolean
}> = ({ label, tip, sortKey, sort, onSort, left }) => {
  const active = sortKey && sort?.key === sortKey
  const arrow = active ? (sort?.dir === 'asc' ? ' ↑' : ' ↓') : ''
  return (
    <th
      title={tip}
      className={`${left ? 'cp-left' : ''} ${sortKey ? 'cp-sortable' : ''}`}
      onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}
      aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {label}
      {arrow}
    </th>
  )
}

const CreatorPerformance: React.FC = () => {
  const [state, setState] = useState<CreatorPerformanceState | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [payback, setPayback] = useState<number | null>(null)
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'subscribers', dir: 'desc' })
  const [now, setNow] = useState(Date.now())
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const describeError = (e: any) =>
    e?.response?.status === 403
      ? 'This dashboard is limited to staff accounts.'
      : 'Could not load creator performance. Try again in a moment.'

  const load = useCallback(async () => {
    try {
      const next = await getCreatorPerformance()
      setState(next)
      setError('')
    } catch (e: any) {
      setError(describeError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const running = state?.run?.status === 'running'

  // Poll while a run is in progress, and tick the elapsed timer every second.
  useEffect(() => {
    if (!running) return undefined
    pollRef.current = setTimeout(load, POLL_MS)
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
      clearInterval(tick)
    }
  }, [running, state, load])

  const payload = state?.payload ?? null
  const effectivePayback = payback ?? payload?.settings.payback_fraction ?? 0.4
  // Server figures are computed at settings.payback_fraction; ETS and signup
  // value are linear in payback, so the input rescales them.
  const paybackScale = payload?.settings.payback_fraction ? effectivePayback / payload.settings.payback_fraction : 0

  const previous = state?.previous ?? null
  const prevChannels = useMemo(
    () => new Map<string, HistoryChannel>((previous?.channels || []).map((c) => [c.key, c])),
    [previous],
  )
  const history = state?.history ?? []

  const onRefresh = async () => {
    setStarting(true)
    try {
      const { run } = await refreshCreatorPerformance()
      setNow(Date.now())
      setState((prev) => ({
        generated_at: prev?.generated_at ?? null,
        payload: prev?.payload ?? null,
        previous: prev?.previous ?? null,
        history: prev?.history ?? [],
        run,
      }))
    } catch (e: any) {
      setError(describeError(e))
    } finally {
      setStarting(false)
    }
  }

  const onSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'channel' || key === 'category' ? 'asc' : 'desc' }))

  const channels = useMemo(() => {
    if (!payload) return []
    const q = filter.trim().toLowerCase()
    const rows = payload.by_channel.filter(
      (r) => !q || r.channel.toLowerCase().includes(q) || r.category.toLowerCase().includes(q),
    )
    const value = (r: ChannelRow): number | string => {
      const e = ets(r, effectivePayback)
      switch (sort.key) {
        case 'ets_p25': return e.p25
        case 'ets_p50': return e.p50
        case 'ets_p75': return e.p75
        case 'channel': return r.channel.toLowerCase()
        case 'category': return r.category.toLowerCase()
        case 'conversion': return conversion(r) ?? -1
        case 'pipeline': return r.pipeline_value_p25
        case 'worth': return r.worth
        default: return (r as any)[sort.key] ?? 0
      }
    }
    return [...rows].sort((a, b) => {
      const va = value(a)
      const vb = value(b)
      const c = va < vb ? -1 : va > vb ? 1 : 0
      return sort.dir === 'asc' ? c : -c
    })
  }, [payload, filter, sort, effectivePayback])

  const header = (
    <div className='cp-header'>
      <div>
        <h1>Creator performance</h1>
        <div className='cp-sub'>
          Refreshed {formatDateTime(state?.generated_at ?? null)}
          {payload ? ` · data to ${payload.snapshot_date}` : ''}
        </div>
        {previous ? (
          <div className='cp-sub' data-testid='previous-note'>
            Changes shown against the refresh of {formatDate(previous.generated_at)}
          </div>
        ) : null}
      </div>
      <div className='cp-actions'>
        <button className='cp-refresh' onClick={onRefresh} disabled={running || starting}>
          {running ? 'Refreshing…' : 'Refresh'}
        </button>
        {running ? (
          <div className='cp-status' role='status'>
            {state?.run.step || 'Starting'} · {formatElapsed(state?.run.started_at ?? null, now)}
          </div>
        ) : null}
      </div>
    </div>
  )

  if (loading) return <div className='cp-page'><div className='cp-wrap'><p className='cp-msg'>Loading…</p></div></div>
  if (error && !state) return <div className='cp-page'><div className='cp-wrap'><p className='cp-msg'>{error}</p></div></div>

  const failed = state?.run?.status === 'failed'

  return (
    <div className='cp-page'>
      <div className='cp-wrap'>
        {header}
        {error ? <p className='cp-error'>{error}</p> : null}
        {failed ? (
          <p className='cp-error'>The last refresh failed: {state?.run.error || 'unknown error'}</p>
        ) : null}

        {!payload ? (
          <p className='cp-msg'>No data yet. Press Refresh to compute the first snapshot (takes a few minutes).</p>
        ) : (
          <>
            <div className='cp-kpis'>
              <div className='cp-kpi'>
                <div className='cp-kpi-label'>Paid subscribers attributed</div>
                <div className='cp-kpi-value'>
                  {int(payload.totals.matched_paid_subscribers)}
                  <span className='cp-muted'> / {int(payload.totals.total_paid_subscribers)}</span>
                </div>
                {payload.totals.matched_from_hints || payload.totals.upgraded_by_hints ? (
                  <div className='cp-muted' data-testid='kpi-hints'>
                    {int(payload.totals.matched_from_hints ?? 0)} from a coupon, affiliate link or ad alone;{' '}
                    {int(payload.totals.upgraded_by_hints ?? 0)} unnamed answers named by one
                  </div>
                ) : null}
              </div>
              <div className='cp-kpi'>
                <div className='cp-kpi-label'>Revenue to date</div>
                <div className='cp-kpi-value'>{money(payload.totals.total_revenue)}</div>
              </div>
              <div className='cp-kpi'>
                <div className='cp-kpi-label'>Spend ceiling, all channels (p50)</div>
                <div className='cp-kpi-value' data-testid='kpi-ets'>
                  {money(payload.by_channel.reduce((s, r) => s + ets(r, effectivePayback).p50, 0))}
                </div>
              </div>
              <div className='cp-kpi'>
                <div className='cp-kpi-label'>Paid users with no answer</div>
                <div className='cp-kpi-value'>{int(payload.totals.unmatched_paid_subscribers)}</div>
              </div>
            </div>

            <label className='cp-payback'>
              Payback fraction
              <input
                type='number'
                step='0.05'
                min='0'
                max='1'
                aria-label='Payback fraction'
                value={effectivePayback}
                onChange={(e) => {
                  const v = parseFloat(e.target.value)
                  setPayback(Number.isFinite(v) ? v : 0)
                }}
              />
              <span className='cp-muted'>Share of lifetime value you are willing to spend to win a subscriber.</span>
            </label>

            <section className='cp-section'>
              <h2>Sponsored creators</h2>
              <div className='cp-scroll'>
                <table className='cp-table' data-testid='partners'>
                  <thead>
                    <tr>
                      <Th label='Creator' left />
                      <Th label='Worth' tip={TIP.worth} />
                      <Th label='Paid subs' tip={TIP.subs} />
                      <Th label='Revenue' tip={TIP.revenue} />
                      <Th label='LTV*' tip={TIP.ltv} />
                      <Th label='ETS p25' tip={TIP.p25} />
                      <Th label='ETS p50' tip={TIP.p50} />
                      <Th label='ETS p75' tip={TIP.p75} />
                      <Th label='Last 90 days' tip={TIP.recent} />
                      <Th label='Signups' tip={TIP.signups} />
                      <Th label='Signup to paid' tip={TIP.conversion} />
                      <Th label='Expected new payers' tip={TIP.newPayers} />
                      <Th label='Signup value' tip={TIP.pipeline} />
                      <Th label='Worth per new signup' tip={TIP.perSignup} />
                    </tr>
                  </thead>
                  <tbody>
                    {payload.partners.map((p) => {
                      const sc = (v: number | null) => (v == null ? null : v * paybackScale)
                      const before = prevChannels.get(p.name)
                      return (
                        <tr key={p.name}>
                          <td className='cp-left'>{p.name}</td>
                          <td className='cp-decision'><Worth row={p} scale={paybackScale} /></td>
                          <td>
                            {int(p.subscribers)}
                            <Delta show={!!previous} now={p.subscribers} before={before?.subscribers} />
                          </td>
                          <td>{money(p.revenue)}</td>
                          <td>{money(p.ltv_star)}</td>
                          <td>{money(sc(p.expected_to_spend_p25))}</td>
                          <td className='cp-decision'>{money(sc(p.expected_to_spend_p50))}</td>
                          <td>{money(sc(p.expected_to_spend_p75))}</td>
                          <td>{int(p.recent_subscribers_90d)}</td>
                          <td>
                            {int(p.signups)}
                            <Delta show={!!previous} now={p.signups} before={before?.signups} />
                          </td>
                          <td>{p.signups ? pct(p.conversion) : '-'}</td>
                          <td>{dec(p.expected_new_payers_p25, 2)}</td>
                          <td>{money(sc(p.pipeline_value_p25))}</td>
                          <td>{money(sc(p.value_per_new_signup_p25))}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className='cp-section'>
              <div className='cp-section-head'>
                <h2>All channels</h2>
                <input
                  className='cp-filter'
                  type='search'
                  placeholder='Filter by channel or category'
                  aria-label='Filter channels'
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
              <div className='cp-scroll'>
                <table className='cp-table' data-testid='channels'>
                  <thead>
                    <tr>
                      <Th label='Channel' left sortKey='channel' sort={sort} onSort={onSort} />
                      <Th label='Category' left sortKey='category' sort={sort} onSort={onSort} />
                      <Th label='Worth' tip={TIP.worth} sortKey='worth' sort={sort} onSort={onSort} />
                      <Th label='Subs' tip={TIP.subs} sortKey='subscribers' sort={sort} onSort={onSort} />
                      <Th label='Revenue' tip={TIP.revenue} sortKey='revenue' sort={sort} onSort={onSort} />
                      <Th label='Avg / sub' tip={TIP.avg} sortKey='avg_revenue' sort={sort} onSort={onSort} />
                      <Th label='Mo %' tip={TIP.mo} sortKey='monthly_share' sort={sort} onSort={onSort} />
                      <Th label='Age (mo)' tip={TIP.age} sortKey='cohort_age_months' sort={sort} onSort={onSort} />
                      <Th label='Maturity' tip={TIP.maturity} sortKey='maturity' sort={sort} onSort={onSort} />
                      <Th label='w' tip={TIP.w} sortKey='shrinkage_w' sort={sort} onSort={onSort} />
                      <Th label='LTV*' tip={TIP.ltv} sortKey='ltv_star' sort={sort} onSort={onSort} />
                      <Th label='ETS p25' tip={TIP.p25} sortKey='ets_p25' sort={sort} onSort={onSort} />
                      <Th label='ETS p50' tip={TIP.p50} sortKey='ets_p50' sort={sort} onSort={onSort} />
                      <Th label='ETS p75' tip={TIP.p75} sortKey='ets_p75' sort={sort} onSort={onSort} />
                      <Th label='Last 90 days' tip={TIP.recent} sortKey='recent_subscribers_90d' sort={sort} onSort={onSort} />
                      <Th label='Signups' tip={TIP.signups} sortKey='signups' sort={sort} onSort={onSort} />
                      <Th label='Signup to paid' tip={TIP.conversion} sortKey='conversion' sort={sort} onSort={onSort} />
                      <Th label='Signup value' tip={TIP.pipeline} sortKey='pipeline' sort={sort} onSort={onSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map((r) => {
                      const e = ets(r, effectivePayback)
                      const before = prevChannels.get(historyKey(r))
                      return (
                        <tr key={`${r.category}|${r.channel}`}>
                          <td className='cp-left'>{r.channel}</td>
                          <td className='cp-left'><CategoryCell row={r} /></td>
                          <td className='cp-decision'><Worth row={r} scale={paybackScale} /></td>
                          <td>
                            {int(r.subscribers)}
                            <Delta show={!!previous} now={r.subscribers} before={before?.subscribers} />
                          </td>
                          <td>{money(r.revenue)}</td>
                          <td>{ifPaid(r, money(r.avg_revenue))}</td>
                          <td>{ifPaid(r, pct(r.monthly_share))}</td>
                          <td>{ifPaid(r, dec(r.cohort_age_months, 1))}</td>
                          <td>{ifPaid(r, dec(r.maturity, 2))}</td>
                          <td>{ifPaid(r, dec(r.shrinkage_w, 2))}</td>
                          <td>{ifPaid(r, money(r.ltv_star))}</td>
                          <td>{ifPaid(r, money(e.p25))}</td>
                          <td className='cp-decision'>{ifPaid(r, money(e.p50))}</td>
                          <td>{ifPaid(r, money(e.p75))}</td>
                          <td>{int(r.recent_subscribers_90d)}</td>
                          <td>
                            {int(r.signups)}
                            <Delta show={!!previous} now={r.signups} before={before?.signups} />
                          </td>
                          <td>{r.signups ? pct(conversion(r) ?? 0) : '-'}</td>
                          <td>{money(r.pipeline_value_p25 * paybackScale)}</td>
                        </tr>
                      )
                    })}
                    {!channels.length ? (
                      <tr><td className='cp-left' colSpan={18}>No channel matches that filter.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className='cp-section'>
              <h2>By category</h2>
              <div className='cp-scroll'>
                <table className='cp-table' data-testid='categories'>
                  <thead>
                    <tr>
                      <Th label='Category' left />
                      <Th label='Worth' tip={TIP.worth} />
                      <Th label='Subs' tip={TIP.subs} />
                      <Th label='Revenue' tip={TIP.revenue} />
                      <Th label='Avg / sub' tip={TIP.avg} />
                      <Th label='Mo %' tip={TIP.mo} />
                      <Th label='Age (mo)' tip={TIP.age} />
                      <Th label='Maturity' tip={TIP.maturity} />
                      <Th label='LTV*' tip={TIP.ltv} />
                      <Th label='ETS p50' tip={TIP.p50} />
                      <Th label='Last 90 days' tip={TIP.recent} />
                      <Th label='Signups' tip={TIP.signups} />
                      <Th label='Signup to paid' tip={TIP.conversion} />
                      <Th label='Signup value' tip={TIP.pipeline} />
                    </tr>
                  </thead>
                  <tbody>
                    {[...payload.by_category]
                      .sort((a, b) => b.subscribers - a.subscribers || b.signups - a.signups)
                      .map((r: CategoryRow) => (
                        <tr key={r.category}>
                          <td className='cp-left'>{r.category}</td>
                          <td className='cp-decision'><Worth row={r} scale={paybackScale} /></td>
                          <td>{int(r.subscribers)}</td>
                          <td>{money(r.revenue)}</td>
                          <td>{ifPaid(r, money(r.avg_revenue))}</td>
                          <td>{ifPaid(r, pct(r.monthly_share))}</td>
                          <td>{ifPaid(r, dec(r.cohort_age_months, 1))}</td>
                          <td>{ifPaid(r, dec(r.maturity, 2))}</td>
                          <td>{ifPaid(r, money(r.ltv_star))}</td>
                          <td className='cp-decision'>{ifPaid(r, money(ets(r, effectivePayback).p50))}</td>
                          <td>{int(r.recent_subscribers_90d)}</td>
                          <td>{int(r.signups)}</td>
                          <td>{r.signups ? pct(conversion(r) ?? 0) : '-'}</td>
                          <td>{money(r.pipeline_value_p25 * paybackScale)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </section>

            {history.length >= 2 ? (
              <section className='cp-section'>
                <h2>Refresh history</h2>
                <div className='cp-scroll'>
                  <table className='cp-table cp-table-narrow' data-testid='history'>
                    <thead>
                      <tr>
                        <Th label='Refreshed' left />
                        <Th label='Data to' left />
                        <Th label='Paid subs attributed' />
                        <Th label='Revenue to date' />
                        <Th label='Global LTV' />
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 8).map((h) => (
                        <tr key={h.generated_at}>
                          <td className='cp-left'>{formatDateTime(h.generated_at)}</td>
                          <td className='cp-left'>{h.snapshot_date}</td>
                          <td>{int(h.matched_paid_subscribers)}</td>
                          <td>{money(h.total_revenue)}</td>
                          <td>{money(h.global_ltv)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            <section className='cp-section cp-prose'>
              <h2>How to read this</h2>
              <p>
                Worth is the quickest check: set it against what you paid. For a channel with paid
                subscribers it is the ETS p50 decision number; for one with none yet it is the
                conservative value of the signups it brought, marked from signups. If you paid more
                than Worth, the spend has not paid back.
              </p>
              <p>
                Compare what a campaign costs, or what a creator is asking for, against that
                channel&apos;s ETS p50. If the cost is below ETS p25, renew at full scope. Between
                p25 and p50, renew. Between p50 and p75, renegotiate the price down. Above p75, pass.
                For deals priced per signup, do not pay more than Worth per new signup.
              </p>
              <p>
                LTV* is the projected lifetime revenue of one subscriber from that channel. Young
                cohorts and small channels lean on their category average until they have enough
                history of their own, which is what the w column shows. The figure they lean on
                is the global LTV, {money(payload.settings.lifetime_value)} per payer, which is
                recomputed on every refresh.
              </p>
              <p>
                Signup value is a separate, deliberately conservative estimate of what signups who
                have not paid yet may still bring. It is not part of the spend ceiling. Most people
                who pay do so within 30 days of signing up, so old signups that never paid are
                worth almost nothing, and a creator with no payers is never credited more than an
                average payer.
              </p>
              <p>
                Each learner is credited to the source they named in the signup survey. Where they
                named none, a tracked affiliate link, a coupon code that names a creator or school,
                or an ad or partner link fills in, in that order. Coupons and affiliate links only
                move paid subscribers, never signups, because people who never paid have neither.
              </p>
              <p data-testid='stores-note'>{storesNote(payload)}</p>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

export default CreatorPerformance
